import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  Badge,
  Button,
  Card,
  DataState,
  InfoRow,
  Modal,
  StatusBadge,
  Table,
  useToast,
  formatDate,
  formatDuration,
  formatLapTime,
  type Column,
} from "@vms/ui-web";
import { GAME_LABELS, parseCarRef } from "@vms/types";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";
import { useOrgCars, useOrgTracks } from "../../api/orgContent";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

interface Lap {
  id: string;
  lapNumber: number;
  lapTimeMs: number;
  sector1Ms: number | null;
  sector2Ms: number | null;
  sector3Ms: number | null;
  isValid: boolean;
  isHidden: boolean;
  position: number | null;
  createdAt: string;
}

interface SessionDetailPayload {
  session: {
    id: string;
    eventId: string;
    driverId: string;
    simulatorId: string | null;
    car: string | null;
    carName?: string | null;
    track: string | null;
    trackName?: string | null;
    status: "pending" | "loading" | "active" | "completed" | "cancelled";
    cancelReason: string | null;
    startedAt: string | null;
    endedAt: string | null;
    createdAt: string;
    multipleChoiceResponse?: string | null;
  };
  driver: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phoneNumber: string | null;
    companyName: string | null;
  } | null;
  simulator: { id: string; name: string } | null;
  event: {
    id: string;
    name: string;
    mode: "hotlap" | "race";
    game: string | null;
    /** Hub-authored key backing the public results URL (`/r/:publicKey/:sessionId`). */
    publicKey?: string | null;
    /** Public results toggle — absent/undefined ⇒ ON; only explicit `false` disables. */
    publicResultsEnabled?: boolean;
  } | null;
  laps: Lap[];
}

function formatCancelReason(reason: string): string {
  switch (reason) {
    case "zero-laps":
      return "No laps recorded";
    case "stationary-timeout":
      return "Driver was idle on-track";
    default:
      return reason;
  }
}

function gameLabel(game: string | null | undefined): string {
  if (!game) return "—";
  return (GAME_LABELS as Record<string, string>)[game] ?? game;
}

export function VenueSessionDetail() {
  const { vslug, sessionId } = useParams<{ vslug: string; sessionId: string }>();
  const { currentOrg, currentVenue } = useOrgContext();
  const { addToast } = useToast();
  const [qrOpen, setQrOpen] = useState(false);
  const detail = useQuery<SessionDetailPayload>(
    currentVenue && sessionId
      ? `/api/venues/${currentVenue.id}/sessions/${sessionId}`
      : null,
  );

  const data = detail.data;

  // Content lists for the car/track visual tiles — same source the leaderboard
  // form uses; images are cloud-hosted (pushed by the hub's asset-uploader).
  const { data: cars } = useOrgCars(currentOrg?.id, data?.event?.game ?? undefined);
  const { data: tracks } = useOrgTracks(currentOrg?.id, data?.event?.game ?? undefined);

  // Public results-page URL for this session's driver. The results page is
  // public (no login) and served from this same origin, so we can build the URL
  // client-side once the event's public_key has synced from the hub. Suppressed
  // when the operator has turned public results off for this event (the
  // `/r/:key/:sessionId` route 404s in that case, so don't offer a dead QR).
  const resultsUrl =
    data?.event?.publicKey && data.event.publicResultsEnabled !== false && sessionId
      ? `${window.location.origin}/r/${data.event.publicKey}/${sessionId}`
      : null;

  const visibleLaps = useMemo(() => (data?.laps ?? []).filter((l) => !l.isHidden), [data]);
  const validLaps = useMemo(() => visibleLaps.filter((l) => l.isValid), [visibleLaps]);

  const stats = useMemo(() => {
    if (validLaps.length === 0) return null;
    const best = validLaps.reduce((b, l) => (l.lapTimeMs < b.lapTimeMs ? l : b));
    const avg = Math.round(
      validLaps.reduce((s, l) => s + l.lapTimeMs, 0) / validLaps.length,
    );
    const bestS1 = bestSector(validLaps, "sector1Ms");
    const bestS2 = bestSector(validLaps, "sector2Ms");
    const bestS3 = bestSector(validLaps, "sector3Ms");
    return { best, avg, bestS1, bestS2, bestS3 };
  }, [validLaps]);

  const durationLabel = useMemo(() => {
    if (!data?.session.startedAt || !data?.session.endedAt) return null;
    const ms =
      new Date(data.session.endedAt).getTime() - new Date(data.session.startedAt).getTime();
    const seconds = Math.floor(ms / 1000);
    return formatDuration(seconds);
  }, [data]);

  // Resolve car/track content (badge, skin preview, track outline) — mirrors
  // hub-ui SessionDetail + the cloud leaderboard detail modal.
  const carInfo = useMemo(() => {
    if (!data || !cars) return null;
    const { carId, skinId } = parseCarRef(data.session.car ?? "");
    const car = cars.find((c) => c.carId === carId);
    if (!car) return null;
    const skin = skinId ? car.skins?.find((s) => s.skinId === skinId) : null;
    const firstPreview = car.skins?.find((s) => s.previewUrl)?.previewUrl;
    return { car, skin, preview: skin?.previewUrl ?? firstPreview };
  }, [data, cars]);

  const trackInfo = useMemo(() => {
    if (!data || !tracks) return null;
    const [trackId, layoutId] = (data.session.track ?? "").split("/");
    const track = tracks.find((t) => t.trackId === trackId);
    if (!track) return null;
    const layout = layoutId ? track.layouts?.find((l) => l.layoutId === layoutId) : null;
    return { track, layout };
  }, [data, tracks]);

  // Lap-time progression (best lap green, invalid red).
  const lapTimeChartData = useMemo(() => {
    const chartLaps = visibleLaps.filter((l) => l.lapTimeMs > 0);
    if (chartLaps.length < 2) return null;
    return {
      labels: chartLaps.map((l) => `Lap ${l.lapNumber}`),
      datasets: [
        {
          label: "Lap Time",
          data: chartLaps.map((l) => l.lapTimeMs / 1000),
          borderColor: "rgb(99, 102, 241)",
          backgroundColor: "rgba(99, 102, 241, 0.1)",
          tension: 0.3,
          pointBackgroundColor: chartLaps.map((l) =>
            !l.isValid
              ? "rgb(239, 68, 68)"
              : stats && l.id === stats.best.id
                ? "rgb(34, 197, 94)"
                : "rgb(99, 102, 241)",
          ),
          pointRadius: 4,
        },
      ],
    };
  }, [visibleLaps, stats]);

  // Position changes (race mode only).
  const positionChartData = useMemo(() => {
    if (data?.event?.mode !== "race") return null;
    const withPosition = visibleLaps.filter((l) => l.position != null);
    if (withPosition.length < 2) return null;
    return {
      labels: withPosition.map((l) => `Lap ${l.lapNumber}`),
      datasets: [
        {
          label: "Position",
          data: withPosition.map((l) => l.position as number),
          borderColor: "rgb(234, 179, 8)",
          backgroundColor: "rgba(234, 179, 8, 0.1)",
          tension: 0.3,
          pointRadius: 4,
        },
      ],
    };
  }, [visibleLaps, data]);

  if (!currentOrg || !currentVenue) return null;

  const backUrl = `/org/${currentOrg.slug}/venue/${vslug}/sessions`;

  return (
    <div>
      <DataState
        loading={detail.loading}
        error={detail.error}
        empty={!detail.loading && !data}
        emptyMessage="Session not found."
      >
        {data && (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <Button variant="secondary" size="sm" to={backUrl}>
                ← Back to Sessions
              </Button>
              {data.driver ? (
                <Link
                  to={`/org/${currentOrg.slug}/drivers/${data.driver.id}`}
                  className="text-2xl font-bold text-gray-900 hover:text-indigo-700"
                >
                  {data.driver.firstName} {data.driver.lastName}
                </Link>
              ) : (
                <h1 className="text-2xl font-bold text-gray-900">Session</h1>
              )}
              <StatusBadge domain="session" status={data.session.status} />
              {data.session.status === "cancelled" && data.session.cancelReason && (
                <span className="text-xs text-gray-500 italic">
                  {formatCancelReason(data.session.cancelReason)}
                </span>
              )}
            </div>

            {/* Info cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <Card className="p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                  Session
                </h2>

                {/* Car visual */}
                <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-gray-50">
                  {carInfo?.preview ? (
                    <div className="w-16 h-12 bg-gray-900 rounded flex-shrink-0 flex items-center justify-center">
                      <img src={carInfo.preview} alt="" className="max-w-full max-h-full object-contain" />
                    </div>
                  ) : carInfo?.car.badgeUrl ? (
                    <img src={carInfo.car.badgeUrl} alt="" className="w-10 h-10 object-contain flex-shrink-0" />
                  ) : null}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {carInfo?.car.name ?? data.session.carName ?? data.session.car ?? "—"}
                    </p>
                    {carInfo?.skin && (
                      <p className="text-xs text-gray-500 truncate">{carInfo.skin.name}</p>
                    )}
                    {carInfo?.car.brand && (
                      <p className="text-xs text-gray-400">{carInfo.car.brand}</p>
                    )}
                  </div>
                </div>

                {/* Track visual — prefer the layout-specific outline/preview. */}
                {(() => {
                  const layoutImage = trackInfo?.layout?.outlineUrl ?? trackInfo?.layout?.previewUrl;
                  const trackOutline = trackInfo?.track.outlineUrl;
                  const isOutline =
                    !!trackInfo?.layout?.outlineUrl || (!layoutImage && !!trackOutline);
                  const imageSrc = layoutImage ?? trackOutline ?? trackInfo?.track.previewUrl;
                  return (
                    <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-gray-50">
                      {imageSrc ? (
                        isOutline ? (
                          <div className="w-12 h-12 bg-gray-800 rounded flex-shrink-0 flex items-center justify-center p-1">
                            <img src={imageSrc} alt="" className="max-w-full max-h-full object-contain" />
                          </div>
                        ) : (
                          <img src={imageSrc} alt="" className="w-12 h-12 object-cover rounded flex-shrink-0" />
                        )
                      ) : null}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {trackInfo?.track.name ?? data.session.trackName ?? data.session.track ?? "—"}
                        </p>
                        {trackInfo?.layout && (
                          <p className="text-xs text-gray-500 truncate">{trackInfo.layout.name}</p>
                        )}
                        {trackInfo?.track.country && (
                          <p className="text-xs text-gray-400">
                            {[trackInfo.track.city, trackInfo.track.country].filter(Boolean).join(", ")}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <dl className="space-y-2 text-sm">
                  {data.event && (
                    <>
                      <InfoRow
                        label="Event"
                        value={
                          <Link
                            to={`/org/${currentOrg.slug}/venue/${vslug}/events/${data.event.id}`}
                            className="text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            {data.event.name}
                          </Link>
                        }
                      />
                      <InfoRow label="Mode" value={data.event.mode} />
                      <InfoRow label="Game" value={gameLabel(data.event.game)} />
                    </>
                  )}
                  {data.simulator && (
                    <InfoRow label="Simulator" value={data.simulator.name} />
                  )}
                  {data.session.startedAt && (
                    <InfoRow label="Started" value={formatDate(data.session.startedAt)} />
                  )}
                  {data.session.endedAt && (
                    <InfoRow label="Ended" value={formatDate(data.session.endedAt)} />
                  )}
                  {durationLabel && <InfoRow label="Duration" value={durationLabel} />}
                </dl>
              </Card>

              <Card className="p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Driver
                </h2>
                {data.driver ? (
                  <dl className="space-y-2 text-sm">
                    <InfoRow
                      label="Name"
                      value={
                        <Link
                          to={`/org/${currentOrg.slug}/drivers/${data.driver.id}`}
                          className="text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          {data.driver.firstName} {data.driver.lastName}
                        </Link>
                      }
                    />
                    {data.driver.email && (
                      <InfoRow label="Email" value={data.driver.email} />
                    )}
                    {data.driver.phoneNumber && (
                      <InfoRow label="Phone" value={data.driver.phoneNumber} />
                    )}
                    {data.driver.companyName && (
                      <InfoRow label="Company" value={data.driver.companyName} />
                    )}
                    {data.session.multipleChoiceResponse && (
                      <InfoRow
                        label="Response"
                        value={data.session.multipleChoiceResponse}
                      />
                    )}
                  </dl>
                ) : (
                  <p className="text-sm text-gray-400">Driver not found.</p>
                )}
                {data.session.status === "completed" && resultsUrl && (
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-end">
                    <Button variant="secondary" size="sm" onClick={() => setQrOpen(true)}>
                      Results QR
                    </Button>
                  </div>
                )}
              </Card>

              <Card className="p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Stats
                </h2>
                {stats ? (
                  <dl className="space-y-2 text-sm">
                    <InfoRow label="Total laps" value={String(visibleLaps.length)} />
                    <InfoRow label="Valid laps" value={String(validLaps.length)} />
                    <InfoRow
                      label="Best lap"
                      value={
                        <span className="font-mono font-semibold text-green-600">
                          {formatLapTime(stats.best.lapTimeMs)}
                        </span>
                      }
                    />
                    <InfoRow
                      label="Average lap"
                      value={<span className="font-mono">{formatLapTime(stats.avg)}</span>}
                    />
                    {stats.bestS1 != null && (
                      <InfoRow
                        label="Best S1"
                        value={<span className="font-mono">{formatLapTime(stats.bestS1)}</span>}
                      />
                    )}
                    {stats.bestS2 != null && (
                      <InfoRow
                        label="Best S2"
                        value={<span className="font-mono">{formatLapTime(stats.bestS2)}</span>}
                      />
                    )}
                    {stats.bestS3 != null && (
                      <InfoRow
                        label="Best S3"
                        value={<span className="font-mono">{formatLapTime(stats.bestS3)}</span>}
                      />
                    )}
                  </dl>
                ) : (
                  <p className="text-sm text-gray-400">No valid laps recorded.</p>
                )}
              </Card>
            </div>

            {/* Lap times */}
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Lap times {visibleLaps.length > 0 && `(${visibleLaps.length})`}
              </h2>
              {visibleLaps.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  No lap times recorded.
                </p>
              ) : (
                <Table<Lap>
                  columns={lapColumns(stats, data.event?.mode === "race")}
                  data={visibleLaps}
                  keyExtractor={(l) => l.id}
                  rowClassName={(l) =>
                    stats && l.id === stats.best.id
                      ? "bg-green-50"
                      : !l.isValid
                        ? "bg-red-50"
                        : ""
                  }
                />
              )}
            </Card>

            {/* Charts */}
            {(lapTimeChartData || positionChartData) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 max-w-4xl">
                {lapTimeChartData && (
                  <Card className="p-4">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Lap Time Progression
                    </h2>
                    <Line
                      data={lapTimeChartData}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => formatLapTime((ctx.parsed.y ?? 0) * 1000),
                            },
                          },
                        },
                        scales: {
                          y: {
                            title: { display: true, text: "Time (s)" },
                            ticks: {
                              callback: (v) => {
                                const val = Number(v);
                                const m = Math.floor(val / 60);
                                const s = (val % 60).toFixed(0);
                                return m > 0 ? `${m}:${s.padStart(2, "0")}` : `${val.toFixed(1)}s`;
                              },
                            },
                          },
                          x: { title: { display: false } },
                        },
                      }}
                    />
                  </Card>
                )}
                {positionChartData && (
                  <Card className="p-4">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Position Changes
                    </h2>
                    <Line
                      data={positionChartData}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            callbacks: { label: (ctx) => `P${ctx.parsed.y}` },
                          },
                        },
                        scales: {
                          y: {
                            reverse: true,
                            title: { display: true, text: "Position" },
                            ticks: { stepSize: 1, callback: (v) => `P${v}` },
                            min: 1,
                          },
                          x: { title: { display: false } },
                        },
                      }}
                    />
                  </Card>
                )}
              </div>
            )}

            {/* Results QR — the public results link for this driver's event
                report. Online link (the public page fetches from the cloud). */}
            {resultsUrl && (
              <Modal isOpen={qrOpen} onClose={() => setQrOpen(false)} title="Results QR code">
                <div className="flex flex-col items-center gap-4 py-2">
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <QRCodeSVG value={resultsUrl} size={224} />
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={resultsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
                    >
                      Open results page
                    </a>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        void navigator.clipboard.writeText(resultsUrl);
                        addToast("Link copied");
                      }}
                    >
                      Copy link
                    </Button>
                  </div>
                </div>
              </Modal>
            )}
          </>
        )}
      </DataState>
    </div>
  );
}

function bestSector(laps: Lap[], key: "sector1Ms" | "sector2Ms" | "sector3Ms"): number | null {
  return laps
    .map((l) => l[key])
    .filter((v): v is number => typeof v === "number" && v > 0)
    .reduce<number | null>((b, v) => (b === null || v < b ? v : b), null);
}

function lapColumns(
  stats: { best: Lap; bestS1: number | null; bestS2: number | null; bestS3: number | null } | null,
  isRace: boolean,
): Column<Lap>[] {
  const cols: Column<Lap>[] = [
    { key: "lapNumber", header: "Lap", render: (l) => l.lapNumber },
    {
      key: "lapTime",
      header: "Time",
      render: (l) => (
        <span className="font-mono font-medium">
          {formatLapTime(l.lapTimeMs)}
          {stats && l.id === stats.best.id && (
            <span className="ml-1 text-green-600 text-xs font-sans font-semibold">BEST</span>
          )}
        </span>
      ),
    },
    {
      key: "s1",
      header: "S1",
      render: (l) => (
        <span
          className={`font-mono text-xs ${stats && l.sector1Ms && l.sector1Ms === stats.bestS1 ? "text-purple-600 font-semibold" : ""}`}
        >
          {l.sector1Ms ? formatLapTime(l.sector1Ms) : "—"}
        </span>
      ),
    },
    {
      key: "s2",
      header: "S2",
      render: (l) => (
        <span
          className={`font-mono text-xs ${stats && l.sector2Ms && l.sector2Ms === stats.bestS2 ? "text-purple-600 font-semibold" : ""}`}
        >
          {l.sector2Ms ? formatLapTime(l.sector2Ms) : "—"}
        </span>
      ),
    },
    {
      key: "s3",
      header: "S3",
      render: (l) => (
        <span
          className={`font-mono text-xs ${stats && l.sector3Ms && l.sector3Ms === stats.bestS3 ? "text-purple-600 font-semibold" : ""}`}
        >
          {l.sector3Ms ? formatLapTime(l.sector3Ms) : "—"}
        </span>
      ),
    },
    {
      key: "valid",
      header: "Valid",
      render: (l) => (
        <Badge
          className={l.isValid ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}
        >
          {l.isValid ? "Valid" : "Invalid"}
        </Badge>
      ),
    },
  ];
  if (isRace) {
    cols.push({
      key: "position",
      header: "Pos",
      render: (l) => (l.position != null ? `P${l.position}` : "—"),
    });
  }
  return cols;
}
